'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { downloadBlob } from '../lib/api/dashboard';
import {
  authorizeBusinessFileReimport,
  confirmBusinessFileImport,
  createBusinessFileImportPreview,
  downloadBusinessFileImportTemplate,
  downloadBusinessFileValidationReport,
  getBusinessFileImportAudit,
  getBusinessFileImportPreview,
  getBusinessFileImportSources,
  getBusinessFileImportTemplate,
  type BusinessFileImportAuditEvent,
  type BusinessFileImportBatch,
  type BusinessFileImportPreview,
  type BusinessFileImportSource,
  type BusinessFileImportTemplate,
} from '../lib/api/business-reports';
import { AdminAlertModal, Loader } from './common';

const PAGE_SIZE = 100;

export function BusinessFileImportsTab({
  active,
  token,
  canManage,
  userName,
}: {
  active: boolean;
  token?: string;
  canManage: boolean;
  userName: string;
}) {
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [sources, setSources] = useState<BusinessFileImportSource[]>([]);
  const [sourceKey, setSourceKey] = useState('');
  const [template, setTemplate] = useState<BusinessFileImportTemplate | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<BusinessFileImportPreview | null>(null);
  const [batch, setBatch] = useState<BusinessFileImportBatch | null>(null);
  const [audit, setAudit] = useState<BusinessFileImportAuditEvent[] | null>(null);
  const [busy, setBusy] = useState('');
  const [dragging, setDragging] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [approvalReference, setApprovalReference] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info' | 'warning'; title: string; message: string } | null>(null);

  const loadSources = useCallback(async () => {
    setBusy('Loading validated file-import sources...');
    try {
      const rows = await getBusinessFileImportSources(token);
      setSources(rows);
      if (rows.length && !rows.some((source) => source.sourceKey === sourceKey)) {
        setSourceKey(rows[0].sourceKey);
      }
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not load import sources', message: errorMessage(error) });
    } finally {
      setBusy('');
    }
  }, [sourceKey, token]);

  useEffect(() => {
    if (active) void loadSources();
  }, [active, loadSources]);

  useEffect(() => {
    if (!active || !sourceKey) {
      setTemplate(null);
      return;
    }
    let current = true;
    setBusy('Loading the approved template contract...');
    void getBusinessFileImportTemplate(sourceKey, token)
      .then((value) => {
        if (current) setTemplate(value);
      })
      .catch((error) => {
        if (current) setAlert({ type: 'error', title: 'Could not load template', message: errorMessage(error) });
      })
      .finally(() => {
        if (current) setBusy('');
      });
    return () => {
      current = false;
    };
  }, [active, sourceKey, token]);

  const selectedSource = sources.find((source) => source.sourceKey === sourceKey) ?? null;
  const canConfirm = Boolean(
    selectedSource?.confirmationAllowed
    && preview?.status === 'READY'
    && preview.validRows + preview.duplicateRows > 0,
  );
  const invalidRows = useMemo(
    () => preview?.rows.filter((row) => row.status !== 'VALID') ?? [],
    [preview],
  );

  if (!active) return null;

  const resetWorkflow = (nextSourceKey = sourceKey) => {
    setSourceKey(nextSourceKey);
    setFile(null);
    setPreview(null);
    setBatch(null);
    setAudit(null);
    setConfirmOpen(false);
    setConfirmChecked(false);
    if (fileInput.current) fileInput.current.value = '';
  };

  const selectFile = (candidate: File | null) => {
    if (!candidate || !selectedSource) return;
    const extension = candidate.name.toLocaleLowerCase('en-IN').slice(candidate.name.lastIndexOf('.'));
    const acceptedExtensions = selectedSource.acceptedExtensions?.length
      ? selectedSource.acceptedExtensions
      : [selectedSource.acceptedExtension];
    if (!acceptedExtensions.includes(extension as '.csv' | '.xlsx')) {
      setAlert({
        type: 'error',
        title: 'File type does not match source',
        message: `${selectedSource.sourceName} accepts only ${acceptedExtensions.join(' or ')} files.`,
      });
      return;
    }
    if (candidate.size < 1 || candidate.size > selectedSource.maxFileSizeBytes) {
      setAlert({
        type: 'error',
        title: 'File size is outside the approved limit',
        message: `Choose a non-empty file no larger than ${formatBytes(selectedSource.maxFileSizeBytes)}.`,
      });
      return;
    }
    setFile(candidate);
    setPreview(null);
    setBatch(null);
    setAudit(null);
  };

  const downloadTemplate = async () => {
    if (!selectedSource) return;
    setBusy('Preparing the approved template...');
    try {
      const result = await downloadBusinessFileImportTemplate(selectedSource.sourceKey, token);
      downloadBlob(result.blob, result.fileName || `${selectedSource.sourceKey}-template${selectedSource.acceptedExtension}`);
    } catch (error) {
      setAlert({ type: 'error', title: 'Template download failed', message: errorMessage(error) });
    } finally {
      setBusy('');
    }
  };

  const createPreview = async () => {
    if (!file || !selectedSource) return;
    setBusy('Validating and staging the governed preview. Large files can take several minutes; no facts are being written...');
    try {
      const result = await createBusinessFileImportPreview(selectedSource.sourceKey, file, token);
      setPreview(result);
      setBatch(null);
      setAudit(null);
    } catch (error) {
      setAlert({ type: 'error', title: 'Preview validation failed', message: errorMessage(error) });
    } finally {
      setBusy('');
    }
  };

  const loadPage = async (skip: number) => {
    if (!preview) return;
    setBusy('Loading preview rows...');
    try {
      setPreview(await getBusinessFileImportPreview(preview.id, token, { skip, take: PAGE_SIZE }));
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not load preview rows', message: errorMessage(error) });
    } finally {
      setBusy('');
    }
  };

  const downloadErrors = async () => {
    if (!preview) return;
    setBusy('Preparing the validation report...');
    try {
      const result = await downloadBusinessFileValidationReport(preview.id, token);
      downloadBlob(result.blob, result.fileName || 'business-report-validation-report.csv');
    } catch (error) {
      setAlert({ type: 'error', title: 'Validation report download failed', message: errorMessage(error) });
    } finally {
      setBusy('');
    }
  };

  const confirmImport = async () => {
    if (!preview || !confirmChecked) return;
    setConfirmOpen(false);
    setBusy('Committing validated rows and mandatory audit records transactionally...');
    try {
      const result = await confirmBusinessFileImport(preview.id, preview.sourceFileChecksumSha256, token);
      setBatch(result.batch);
      setPreview(await getBusinessFileImportPreview(preview.id, token, { take: PAGE_SIZE }));
      setAlert({
        type: 'success',
        title: result.idempotent ? 'Import already completed' : 'Import completed',
        message: `${result.batch.importedRows} new fact row(s) were committed in batch ${result.batch.id}.`,
      });
    } catch (error) {
      setAlert({ type: 'error', title: 'Import was not committed', message: errorMessage(error) });
    } finally {
      setConfirmChecked(false);
      setBusy('');
    }
  };

  const authorizeReimport = async () => {
    if (!preview) return;
    setBusy('Recording separate-manager re-import authorization...');
    try {
      const updated = await authorizeBusinessFileReimport(preview.id, {
        approvalReference,
        reason: overrideReason,
      }, token);
      setPreview({ ...preview, ...updated });
      setOverrideOpen(false);
      setApprovalReference('');
      setOverrideReason('');
      setAlert({
        type: 'success',
        title: 'Re-import authorized',
        message: 'The checksum block was released. Confirmation still requires an explicit checksum review.',
      });
    } catch (error) {
      setAlert({ type: 'error', title: 'Re-import authorization failed', message: errorMessage(error) });
    } finally {
      setBusy('');
    }
  };

  const openAudit = async () => {
    if (!preview) return;
    setBusy('Loading the mandatory audit trail...');
    try {
      setAudit(await getBusinessFileImportAudit(preview.id, token));
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not load audit trail', message: errorMessage(error) });
    } finally {
      setBusy('');
    }
  };

  return (
    <section className="tab-content business-source-page business-file-import-page" id="tab-business-file-imports">
      {busy && <Loader message={busy} />}

      <div className="business-page-toolbar business-file-import-hero">
        <div className="business-file-import-stepper" aria-label="Import steps">
          <span className={preview ? 'done' : 'current'}>1 · Select</span>
          <span className={preview ? (batch ? 'done' : 'current') : ''}>2 · Validate</span>
          <span className={batch ? 'done' : ''}>3 · Commit</span>
        </div>
      </div>

      {!sources.length && !busy ? (
        <div className="enterprise-card business-source-empty" data-testid="file-import-empty-state">
          <div className="business-source-empty-icon">↑</div>
          <h3>No validated upload source is configured</h3>
          <p>A manager must configure and validate a CSV or Excel source in Data Sources first.</p>
          <button className="btn btn-outline" onClick={() => void loadSources()}>Check again</button>
        </div>
      ) : (
        <>
          <div className="enterprise-card business-file-import-setup">
            <div className="enterprise-card-heading">
              <div>
                <strong>1. Source and approved template</strong>
                <span>Validated sources may be previewed while inactive; confirmation requires source activation.</span>
              </div>
            </div>
            <div className="business-file-import-setup-grid">
              <label className="business-source-field">
                <span>Import source</span>
                <select
                  aria-label="Import source"
                  value={sourceKey}
                  onChange={(event) => resetWorkflow(event.target.value)}
                >
                  {sources.map((source) => (
                    <option key={source.sourceKey} value={source.sourceKey}>
                      {source.sourceName} · {source.sourceType === 'CSV_IMPORT' ? 'CSV' : 'Excel'}{source.isActive ? '' : ' · preview only'}
                    </option>
                  ))}
                </select>
              </label>
              <div className="business-file-import-template">
                <div>
                  <strong>Template v{template?.version || selectedSource?.templateVersion}</strong>
                  <span>
                    {template?.headers.length ?? 0} columns · up to {selectedSource?.maxRows.toLocaleString('en-IN')} rows · {formatBytes(selectedSource?.maxFileSizeBytes ?? 0)}
                  </span>
                </div>
                <button className="btn btn-outline" disabled={!template} onClick={() => void downloadTemplate()}>
                  Download {selectedSource?.acceptedExtension.toUpperCase()} template
                </button>
              </div>
            </div>
          </div>

          <div className="enterprise-card business-file-import-upload-card">
            <div className="enterprise-card-heading">
              <div>
                <strong>2. Upload and parse</strong>
                <span>Uploading creates a temporary normalized preview. It does not write facts.</span>
              </div>
            </div>
            <div
              className={`business-file-dropzone${dragging ? ' dragging' : ''}${file ? ' selected' : ''}`}
              onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
              onDragOver={(event) => event.preventDefault()}
              onDragLeave={(event) => { event.preventDefault(); setDragging(false); }}
              onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                selectFile(event.dataTransfer.files.item(0));
              }}
            >
              <input
                ref={fileInput}
                type="file"
                accept={selectedSource?.acceptedExtensions?.join(',') || selectedSource?.acceptedExtension}
                onChange={(event) => selectFile(event.target.files?.item(0) ?? null)}
              />
              <div className="business-file-drop-icon">⇧</div>
              {file ? (
                <>
                  <strong>{file.name}</strong>
                  <span>{formatBytes(file.size)} · modified {formatDateTime(new Date(file.lastModified).toISOString())}</span>
                </>
              ) : (
                <>
                  <strong>Drop {selectedSource?.acceptedExtensions?.join(' or ') || selectedSource?.acceptedExtension} here</strong>
                  <span>or use the file picker. Approved source fields are privately staged; sensitive fields are never shown in Business Reports.</span>
                </>
              )}
              <div className="business-file-import-actions">
                <button className="btn btn-outline" onClick={() => fileInput.current?.click()}>
                  {file ? 'Choose a different file' : 'Choose file'}
                </button>
                <button className="btn btn-primary" disabled={!file} onClick={() => void createPreview()}>
                  Parse and validate
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {preview && (
        <div className="enterprise-card business-file-import-preview" data-testid="file-import-preview">
          <div className="enterprise-card-heading">
            <div>
              <strong>3. Validation summary</strong>
              <span>{preview.sourceFileName} · checksum {shortHash(preview.sourceFileChecksumSha256)} · expires {formatDateTime(preview.expiresAt)}</span>
            </div>
            <span className={`business-source-status ${preview.status === 'READY' ? 'valid' : preview.status === 'CONFIRMED' ? 'active' : 'warning'}`}>
              {label(preview.status)}
            </span>
          </div>

          <div className="business-file-import-kpis">
            <Summary label="Total rows" value={preview.totalRows} tone="neutral" />
            <Summary label="Valid" value={preview.validRows} tone="success" />
            <Summary label="Invalid" value={preview.invalidRows} tone="danger" />
            <Summary label="Duplicates" value={preview.duplicateRows} tone="warning" />
          </div>

          <div className="business-file-date-review" data-testid="file-import-business-dates">
            <div>
              <span>Business-date coverage</span>
              <strong>{formatBusinessDateRange(preview.sourceBusinessDateFrom, preview.sourceBusinessDateTo)}</strong>
            </div>
            <p>Dates are read from the approved source columns and interpreted using the source&apos;s governed business-date contract.</p>
          </div>

          <FinancialPreviewComparison preview={preview} />

          <div className={`business-file-import-policy ${preview.status === 'REIMPORT_BLOCKED' ? 'blocked' : ''}`}>
            <strong>Duplicate and replacement policy</strong>
            <p>
              Duplicate rows are idempotent and are not inserted again. Existing immutable facts are never replaced.
              {preview.status === 'REIMPORT_BLOCKED'
                ? ` This exact source checksum already produced batch ${preview.duplicateOfImportBatchId}; a separate manager must authorize re-import.`
                : ' Any conflicting immutable fact stops confirmation and requires a new corrected preview.'}
            </p>
            {preview.status === 'REIMPORT_BLOCKED' && canManage && (
              <button className="btn btn-outline" onClick={() => setOverrideOpen(true)}>Authorize re-import</button>
            )}
            {preview.status === 'REIMPORT_BLOCKED' && !canManage && (
              <span className="business-file-import-note">Ask a different Business Reports manager to authorize this preview.</span>
            )}
          </div>

          {preview.rows.length > 0 && (
            <div className="business-file-validation-section">
              <div className="business-file-validation-heading">
                <div>
                  <strong>Normalized preview rows on this page</strong>
                  <span>Valid rows are eligible for import, invalid rows are quarantined, and duplicates are skipped.</span>
                </div>
                {invalidRows.length > 0 && (
                  <button className="btn btn-outline btn-sm" onClick={() => void downloadErrors()}>
                    Download validation CSV
                  </button>
                )}
              </div>
              <div className="enterprise-table-wrap">
                <table className="business-source-table">
                  <thead>
                    <tr>
                      <th>Row</th><th>Status</th><th>Date / centre</th><th>Revenue source / reference</th><th>Values</th><th>Messages</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((row) => (
                      <tr key={row.rowNumber}>
                        <td>{row.rowNumber}</td>
                        <td><span className={`business-source-status ${previewRowStatusTone(row.status)}`}>{label(row.status)}</span></td>
                        <td>{row.businessDate || '—'}<br /><span className="business-source-secondary">{row.center?.displayName || 'Unresolved centre'}</span></td>
                        <td>{row.revenueSource?.sourceName || 'Unresolved source'}{row.sourceCategory ? ` · ${row.sourceCategory}` : ''}<br /><span className="business-source-secondary">{row.sourceTransactionType || 'Unknown transaction'} · {row.sourceRecordReference || 'No reference'}</span></td>
                        <td>{row.currency || '—'} {row.sourceAmount || row.netRevenue || '—'}<br /><span className="business-source-secondary">{row.sourceAmountField === 'TAXABLE_AMOUNT' ? 'Taxable Amt' : row.sourceAmountField === 'NET_AMOUNT' ? 'Net Amt' : 'Normalized net'}</span></td>
                        <td>{row.diagnostics.map((diagnostic) => diagnostic.message).join(' · ') || 'No diagnostic'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="business-file-import-pagination">
            <span>Rows {preview.rowPage.skip + 1}–{preview.rowPage.skip + preview.rowPage.returned} of {preview.totalRows}</span>
            <div>
              <button className="btn btn-outline btn-sm" disabled={preview.rowPage.skip === 0} onClick={() => void loadPage(Math.max(0, preview.rowPage.skip - PAGE_SIZE))}>Previous</button>
              <button className="btn btn-outline btn-sm" disabled={!preview.rowPage.hasMore} onClick={() => void loadPage(preview.rowPage.skip + preview.rowPage.take)}>Next</button>
            </div>
          </div>

          {!batch && (
            <div className="business-file-import-commit">
              <div>
                <strong>Ready to commit?</strong>
                <span>{preview.validRows} valid row(s) will be considered; invalid rows are excluded and duplicates are skipped.</span>
              </div>
              <button className="btn btn-primary" disabled={!canConfirm} onClick={() => setConfirmOpen(true)}>
                Review and confirm import
              </button>
              {!selectedSource?.confirmationAllowed && <span className="business-file-import-note">Preview is available, but confirmation remains disabled until the source and its revenue streams are activated after reconciliation and Finance sign-off.</span>}
            </div>
          )}
        </div>
      )}

      {batch && preview && (
        <div className="enterprise-card business-file-import-complete" data-testid="file-import-complete">
          <div className="business-file-complete-mark">✓</div>
          <div>
            <p className="eyebrow">Import completed</p>
            <h3>{batch.id}</h3>
            <p>{batch.importedRows} new row(s) committed from {preview.dataSource.sourceName} at {formatDateTime(batch.completedAt)}.</p>
            <div className="business-file-batch-meta">
              <span><b>Batch reference</b>{batch.batchReference}</span>
              <span><b>Source</b>{preview.dataSource.sourceName}</span>
              <span><b>Import time</b>{formatDateTime(batch.completedAt)}</span>
              <span><b>File checksum</b>{shortHash(batch.sourceFileChecksumSha256 || '')}</span>
            </div>
            <div className="business-file-total-list">
              {batch.summaryTotals.length
                ? batch.summaryTotals.map((total) => (
                  <span key={total.currency}><b>{total.currency}</b> Net {formatAmount(total.netRevenue)}</span>
                ))
                : <span>No monetary totals were recorded.</span>}
            </div>
            <a href="#business-file-import-audit" onClick={(event) => { event.preventDefault(); void openAudit(); }}>
              View mandatory audit trail
            </a>
            <button className="btn btn-outline btn-sm" onClick={() => resetWorkflow()}>Start another import</button>
          </div>
        </div>
      )}

      {confirmOpen && preview && (
        <div className="business-source-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="confirm-file-import-title">
          <div className="business-file-import-modal">
            <div className="business-source-modal-header">
              <p className="eyebrow">Governed import review</p>
              <h3 id="confirm-file-import-title">Import confirmation</h3>
              <p>This confirms an operational source import only. It is not Finance approval of the source values.</p>
              <button className="admin-modal-close" aria-label="Close confirmation" onClick={() => setConfirmOpen(false)}>&times;</button>
            </div>
            <div className="business-file-import-modal-body">
              <dl>
                <div><dt>Source</dt><dd>{preview.dataSource.sourceName}</dd></div>
                <div><dt>File</dt><dd>{preview.sourceFileName}</dd></div>
                <div><dt>Valid / invalid / duplicate</dt><dd>{preview.validRows} / {preview.invalidRows} / {preview.duplicateRows}</dd></div>
                <div><dt>SHA-256</dt><dd className="business-file-hash">{preview.sourceFileChecksumSha256}</dd></div>
              </dl>
              <label className="business-file-confirm-check">
                <input type="checkbox" checked={confirmChecked} onChange={(event) => setConfirmChecked(event.target.checked)} />
                <span>I reviewed the source, business dates, centre totals, prior-import comparison, duplicate policy and exact file checksum. I confirm this import only; this is not Finance approval.</span>
              </label>
            </div>
            <div className="business-source-modal-footer">
              <button className="btn btn-outline" onClick={() => setConfirmOpen(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={!confirmChecked} onClick={() => void confirmImport()}>Confirm import</button>
            </div>
          </div>
        </div>
      )}

      {overrideOpen && preview && (
        <div className="business-source-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="authorize-reimport-title">
          <div className="business-file-import-modal">
            <div className="business-source-modal-header">
              <p className="eyebrow">Separate-manager control</p>
              <h3 id="authorize-reimport-title">Authorize checksum re-import</h3>
              <p>Creator: {preview.createdBy}. Current manager: {userName || 'Unknown'}.</p>
              <button className="admin-modal-close" aria-label="Close authorization" onClick={() => setOverrideOpen(false)}>&times;</button>
            </div>
            <div className="business-file-import-modal-body">
              <label className="business-source-field">
                <span>Approval reference</span>
                <input value={approvalReference} onChange={(event) => setApprovalReference(event.target.value)} maxLength={255} />
              </label>
              <label className="business-source-field">
                <span>Reason (minimum 10 characters)</span>
                <textarea value={overrideReason} onChange={(event) => setOverrideReason(event.target.value)} rows={4} maxLength={1000} />
              </label>
            </div>
            <div className="business-source-modal-footer">
              <button className="btn btn-outline" onClick={() => setOverrideOpen(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={!approvalReference.trim() || overrideReason.trim().length < 10} onClick={() => void authorizeReimport()}>Authorize</button>
            </div>
          </div>
        </div>
      )}

      {audit && (
        <div className="business-source-modal-overlay" id="business-file-import-audit" role="dialog" aria-modal="true" aria-labelledby="file-import-audit-title">
          <div className="business-file-import-modal wide">
            <div className="business-source-modal-header">
              <p className="eyebrow">Immutable database audit</p>
              <h3 id="file-import-audit-title">Preview and batch audit trail</h3>
              <p>{audit.length} event(s) linked to preview {preview?.id} and its completed batch.</p>
              <button className="admin-modal-close" aria-label="Close audit trail" onClick={() => setAudit(null)}>&times;</button>
            </div>
            <div className="enterprise-table-wrap business-file-audit-table">
              <table>
                <thead><tr><th>Time</th><th>Action</th><th>Actor</th><th>Record</th></tr></thead>
                <tbody>
                  {audit.map((event) => (
                    <tr key={event.id}>
                      <td>{formatDateTime(event.createdAt)}</td>
                      <td>{event.action}</td>
                      <td>{event.actor}</td>
                      <td>{event.aggregateType}<br /><span className="business-source-secondary">{event.aggregateId}</span></td>
                    </tr>
                  ))}
                  {!audit.length && <tr><td colSpan={4}>No audit event was returned.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {alert && (
        <AdminAlertModal
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </section>
  );
}

function FinancialPreviewComparison({ preview }: { preview: BusinessFileImportPreview }) {
  const summary = preview.financialSummary;
  const latestPriorBatch = summary.priorImportBatches[0];
  return (
    <div className="business-file-financial-review" data-testid="file-import-financial-summary">
      <div className="business-file-validation-heading">
        <div>
          <strong>Centre-wise financial review</strong>
          <span>Complete validated source-file totals are grouped by centre and currency. Different currencies are never combined.</span>
        </div>
        <span className={`business-source-status ${summary.hasPriorImports ? 'warning' : 'valid'}`}>
          {summary.hasPriorImports ? `${summary.priorImportBatches.length} prior batch(es)` : 'No prior import'}
        </span>
      </div>

      <div className="business-file-currency-comparisons">
        {summary.currencyTotals.map((total) => (
          <div key={total.currency}>
            <span>{total.currency} file total</span>
            <strong>{formatAmount(total.preview.netRevenue)}</strong>
            <small>
              Prior {formatAmount(total.prior.netRevenue)}
              {' · '}
              Change {formatSignedAmount(total.variance.netRevenue)}
            </small>
          </div>
        ))}
        {!summary.currencyTotals.length && (
          <p>No valid financial rows are available for comparison. Resolve the validation errors before confirmation.</p>
        )}
      </div>

      <div className="business-file-prior-note">
        {latestPriorBatch ? (
          <>
            <strong>Prior-import basis</strong>
            <span>
              Existing immutable facts for the same source and business-date range across {summary.priorImportBatches.length} completed batch(es).
              Latest: {latestPriorBatch.batchReference}
              {latestPriorBatch.sourceFileName ? ` · ${latestPriorBatch.sourceFileName}` : ''}
              {latestPriorBatch.completedAt ? ` · ${formatDateTime(latestPriorBatch.completedAt)}` : ''}.
            </span>
          </>
        ) : (
          <>
            <strong>No prior imported facts for this source and date range</strong>
            <span>The prior columns remain zero; they are not treated as a Finance-approved baseline.</span>
          </>
        )}
      </div>

      {summary.categoryComparisons.length > 0 && (
        <div className="enterprise-table-wrap">
          <table className="business-source-table business-file-comparison-table">
            <thead><tr><th>Source category</th><th>Uploaded rows</th><th>Uploaded amount</th><th>Prior amount</th><th>Change</th></tr></thead>
            <tbody>
              {summary.categoryComparisons.map((row) => (
                <tr key={`${row.sourceKey}-${row.currency}`}>
                  <td><strong>{row.sourceName}</strong><br /><span className="business-source-secondary">{row.sourceKey === 'PHARMACY' ? 'Signed Taxable Amt before GST' : row.sourceKey === 'MAIN' ? 'Signed Net Amt' : `Governed category ${row.sourceKey}`}</span></td>
                  <td>{row.preview.rowCount.toLocaleString('en-IN')}</td>
                  <td>{row.currency} {formatAmount(row.preview.netRevenue)}</td>
                  <td>{row.currency} {formatAmount(row.prior.netRevenue)}</td>
                  <td className={amountTone(row.variance.netRevenue)}>{formatSignedAmount(row.variance.netRevenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {summary.centerComparisons.length > 0 && (
        <div className="enterprise-table-wrap">
          <table className="business-source-table business-file-comparison-table">
            <thead>
              <tr>
                <th>Centre</th>
                <th>Currency</th>
                <th>Uploaded file</th>
                <th>Prior imports</th>
                <th>Change</th>
              </tr>
            </thead>
            <tbody>
              {summary.centerComparisons.map((row) => (
                <tr key={`${row.centerCode}-${row.currency}`}>
                  <td>
                    <strong>{row.displayName}</strong>
                    <small>Code {row.centerCode}</small>
                  </td>
                  <td><strong>{row.currency}</strong></td>
                  <td>
                    <strong>{formatAmount(row.preview.netRevenue)}</strong>
                    <small>{row.preview.rowCount} row(s) · Gross {formatAmount(row.preview.grossRevenue)} · Discount {formatAmount(row.preview.discount)} · Refund {formatAmount(row.preview.refund)}</small>
                  </td>
                  <td>
                    <strong>{formatAmount(row.prior.netRevenue)}</strong>
                    <small>{row.prior.rowCount} existing fact(s) · Gross {formatAmount(row.prior.grossRevenue)} · Discount {formatAmount(row.prior.discount)} · Refund {formatAmount(row.prior.refund)}</small>
                  </td>
                  <td>
                    <strong className={amountTone(row.variance.netRevenue)}>{formatSignedAmount(row.variance.netRevenue)}</strong>
                    <small>Net revenue difference</small>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Summary({ label: text, value, tone }: { label: string; value: number; tone: string }) {
  return <div className={`business-file-import-summary ${tone}`}><span>{text}</span><strong>{value.toLocaleString('en-IN')}</strong></div>;
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : 'An unexpected error occurred.';
}

function formatBytes(value: number) {
  if (!value) return '0 bytes';
  if (value >= 1_048_576) return `${(value / 1_048_576).toFixed(value % 1_048_576 ? 1 : 0)} MiB`;
  if (value >= 1024) return `${(value / 1024).toFixed(1)} KiB`;
  return `${value} bytes`;
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Kolkata' });
}

function formatAmount(value: string) {
  const parsed = Number(value);
  return Number.isFinite(parsed)
    ? parsed.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : value;
}

function formatSignedAmount(value: string) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return value;
  const formatted = Math.abs(parsed).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (parsed > 0) return `+${formatted}`;
  if (parsed < 0) return `-${formatted}`;
  return formatted;
}

function amountTone(value: string) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed === 0) return 'neutral';
  return parsed > 0 ? 'positive' : 'negative';
}

function previewRowStatusTone(status: BusinessFileImportPreview['rows'][number]['status']) {
  if (status === 'VALID') return 'valid';
  if (status === 'INVALID') return 'invalid';
  return 'inactive';
}

function formatBusinessDateRange(from: string | null, to: string | null) {
  if (!from && !to) return 'No valid business date detected';
  if (from === to || !to) return from || to || 'No valid business date detected';
  return `${from} to ${to}`;
}

function shortHash(value: string) {
  return value ? `${value.slice(0, 12)}…${value.slice(-8)}` : '—';
}

function label(value: string) {
  return value.toLocaleLowerCase('en-IN').replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toLocaleUpperCase('en-IN'));
}
